if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer') && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';

      this.initEditMode();
    }

    async initEditMode() {
      const urlParams = new URLSearchParams(window.location.search);
      const lineIndex = urlParams.get('edit_cart_line');
      if (!lineIndex) return;

      this.editLineIndex = parseInt(lineIndex);

      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const item = cart.items[this.editLineIndex - 1];

        const currentHandle = window.location.pathname.split('/').filter(Boolean).pop();
        if (!item || item.handle !== currentHandle) {
          console.warn('Edit line item mismatch or not found', { itemHandle: item?.handle, currentHandle });
          return;
        }

        // Pre-fill form fields
        this.prefillForm(item.properties);

        // Change button text
        if (this.submitButton) {
          const span = this.submitButton.querySelector('span');
          if (span) span.textContent = 'Update Cart';
          this.submitButton.dataset.buttonText = 'Update Cart';
        }

      } catch (error) {
        console.error('Error initializing edit mode:', error);
      }
    }

    prefillForm(properties) {
      if (!properties) return;

      // Detect if this is a bundle product by checking for bundle properties
      const isBundle = Object.keys(properties).some(key =>
        key.startsWith('Recipient ') ||
        key.startsWith('Who From ') ||
        key.startsWith('Recipient Phone ') ||
        key.startsWith('Occasion ') ||
        key.startsWith('Message ')
      );

      // Gifting toggle - handle checkbox first to show/hide fields.
      // For older bundle items that may not have `is_gift`, treat any bundle-detail properties as "gift enabled"
      // so the bundle configuration UI is visible when editing from cart.
      const hasBundleDetails = isBundle && Object.entries(properties).some(([k, v]) => {
        if (k === 'is_gift') return false;
        return v != null && String(v).trim() !== '';
      });
      const isGift = properties['is_gift'] === 'true' || hasBundleDetails;
      const formId = this.form?.id;
      const singleGiftCheckbox = formId
        ? document.querySelector(`[form="${formId}"][id^="IsGiftToggle-"][name="properties[is_gift]"]`)
        : null;
      const giftCheckboxes = [
        ...Array.from(this.form.querySelectorAll('[name="properties[is_gift]"]')),
        ...(formId ? Array.from(document.querySelectorAll(`[form="${formId}"][name="properties[is_gift]"]`)) : []),
      ];
      const targetGiftCheckbox = singleGiftCheckbox || giftCheckboxes.find((cb) => !cb.disabled) || giftCheckboxes[0];

      if (targetGiftCheckbox) {
        targetGiftCheckbox.checked = isGift;
        // Dispatch change event to show/hide gifting fields (and bundle gifting UI on standard template)
        targetGiftCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Pre-fill all property fields
      for (const [key, value] of Object.entries(properties)) {
        // Skip is_gift as it's already handled above
        if (key === 'is_gift') continue;

        // Handle bundle properties (e.g., Recipient 1, Occasion 2, Message 3, etc.)
        if (isBundle && (key.startsWith('Recipient ') || key.startsWith('Who From ') ||
          key.startsWith('Recipient Phone ') || key.startsWith('Occasion ') || key.startsWith('Message '))) {
          // Extract index from property name (e.g., "Recipient 1" -> 1, "Recipient Phone 2" -> 2)
          // Match the last space followed by digits
          const match = key.match(/ (\d+)$/);
          if (match) {
            const index = match[1]; // e.g., "1", "2", "3"

            // Map property keys to input selectors
            let selector;
            if (key.startsWith('Recipient Phone ')) {
              selector = `[name="properties[Recipient Phone ${index}]"]`;
            } else if (key.startsWith('Recipient ')) {
              selector = `[name="properties[Recipient ${index}]"]`;
            } else if (key.startsWith('Who From ')) {
              selector = `[name="properties[Who From ${index}]"]`;
            } else if (key.startsWith('Occasion ')) {
              selector = `[name="properties[Occasion ${index}]"]`;
            } else if (key.startsWith('Message ')) {
              selector = `[name="properties[Message ${index}]"]`;
            }

            if (selector) {
              const input = this.form.querySelector(selector) || document.querySelector(`[form="${this.form.id}"]${selector}`);
              if (input) {
                input.value = value;
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('change', { bubbles: true }));

                // For textarea (Card Message), ensure character counter updates
                if (input.tagName === 'TEXTAREA') {
                  setTimeout(() => {
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                  }, 50);
                }
              }
            }
          }
          continue;
        }

        // Handle delivery address properties (e.g., "Bouquet 1 - Delivery Address Line 1")
        if (isBundle && key.includes('Bouquet') && key.includes('Delivery')) {
          const input = this.form.querySelector(`[name="properties[${key}]"]`) ||
            document.querySelector(`[form="${this.form.id}"][name="properties[${key}]"]`);
          if (input) {
            input.value = value;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          continue;
        }

        // Handle single product properties and other properties
        const input = this.form.querySelector(`[name="properties[${key}]"]`) ||
          document.querySelector(`[form="${this.form.id}"][name="properties[${key}]"]`);
        if (input) {
          input.value = value;

          // For textarea (Card Message), ensure character counter updates
          if (input.tagName === 'TEXTAREA' && input.name.includes('Card Message')) {
            // Dispatch input event to trigger character counter update
            input.dispatchEvent(new Event('input', { bubbles: true }));
          } else {
            // For other inputs, dispatch both input and change events
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      // Special handling for bundle dates in Flatpickr if it's on the page
      const selectedDatesHidden = this.form.querySelector('[name="properties[Selected_Dates]"]') ||
        document.querySelector(`[form="${this.form.id}"][name="properties[Selected_Dates]"]`);
      if (selectedDatesHidden && properties['Selected_Dates']) {
        selectedDatesHidden.value = properties['Selected_Dates'];
        // Wait a bit for Flatpickr to init if it's async
        setTimeout(() => {
          const calendarInput = this.form.querySelector('.bundle-calendar-input') ||
            document.querySelector(`[form="${this.form.id}"].bundle-calendar-input`);
          if (calendarInput && calendarInput._flatpickr) {
            const dates = properties['Selected_Dates'].split(', ').filter(d => d);
            calendarInput._flatpickr.setDate(dates);
          }
        }, 500);
      }

      // Ensure character counter is updated after a short delay to account for DOM ready
      // This handles cases where the gifting form script hasn't fully initialized yet
      setTimeout(() => {
        // Check for single product card message
        const cardMessage = this.form.querySelector('[name="properties[Card Message]"]') ||
          document.querySelector(`[form="${this.form.id}"][name="properties[Card Message]"]`);
        if (cardMessage && cardMessage.value) {
          // Trigger input event again to ensure character counter updates
          cardMessage.dispatchEvent(new Event('input', { bubbles: true }));
        }

        // Check for bundle messages (Message 1, Message 2, etc.)
        if (isBundle) {
          for (let i = 1; i <= 10; i++) {
            const bundleMessage = this.form.querySelector(`[name="properties[Message ${i}]"]`) ||
              document.querySelector(`[form="${this.form.id}"][name="properties[Message ${i}]"]`);
            if (bundleMessage && bundleMessage.value) {
              bundleMessage.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        }
      }, 100);
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      // Validate gifting form fields if gift checkbox is checked
      const formId = this.form.id;
      if (formId) {
        const giftCheckboxes = [
          ...Array.from(this.form.querySelectorAll('[name="properties[is_gift]"]')),
          ...Array.from(document.querySelectorAll(`[form="${formId}"][name="properties[is_gift]"]`)),
        ];
        const isGiftChecked = giftCheckboxes.some((cb) => cb.checked);

        // Detect bundle size for validation (mirrors the detection used later for filtering)
        let bundleSizeForValidation = 0;
        const bundleConfigContainer = document.querySelector('[id^="BundleConfiguration-"]');
        if (bundleConfigContainer) {
          bundleSizeForValidation = parseInt(bundleConfigContainer.dataset.bundleSize) || 0;
        }
        if (bundleSizeForValidation === 0) {
          const variantInput = this.form.querySelector('input[name="id"]:checked, input[name="id"]');
          if (variantInput) {
            const variantId = variantInput.value;
            const fieldsets = this.form.querySelectorAll('variant-selects fieldset, variant-radios fieldset');
            for (const fieldset of fieldsets) {
              const label = fieldset.querySelector('legend, label');
              const optionName = label ? (label.textContent || '').trim().toLowerCase() : '';

              if (optionName === 'bundle size' || optionName === 'size' || optionName === 'frequency') {
                const select = fieldset.querySelector('select[name^="options"]');
                const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
                const value = (select ? select.value : (checkedRadio ? checkedRadio.value : '')).toLowerCase();

                if (value.includes('10')) {
                  bundleSizeForValidation = 10;
                  break;
                } else if (value.includes('8')) {
                  bundleSizeForValidation = 8;
                  break;
                } else if (value.includes('5')) {
                  bundleSizeForValidation = 5;
                  break;
                }
              }
            }

            if (bundleSizeForValidation === 0 && variantId) {
              const variantOption = this.form.querySelector(`option[value="${variantId}"]`);
              if (variantOption) {
                const variantText = variantOption.textContent.toLowerCase();
                if (variantText.includes('10')) bundleSizeForValidation = 10;
                else if (variantText.includes('8')) bundleSizeForValidation = 8;
                else if (variantText.includes('5')) bundleSizeForValidation = 5;
              }
            }
          }
        }

        if (isGiftChecked) {
          const errors = [];

          // Single-product gifting validation (standard gifting-form)
          if (bundleSizeForValidation === 0) {
            const recipientName =
              document.querySelector(`[form="${formId}"][name="properties[Recipient Name]"]`) ||
              this.form.querySelector('[name="properties[Recipient Name]"]');
            const cardMessage =
              document.querySelector(`[form="${formId}"][name="properties[Card Message]"]`) ||
              this.form.querySelector('[name="properties[Card Message]"]');

            if (!recipientName || !recipientName.value || !recipientName.value.trim()) {
              errors.push('Recipient Name is required when this is a gift.');
            }

          } else {
            // Bundle gifting validation (bouquet-by-bouquet configuration)
            const recipient1 = document.querySelector(`[form="${formId}"][name="properties[Recipient 1]"]`);
            const message1 = document.querySelector(`[form="${formId}"][name="properties[Message 1]"]`);

            if (!recipient1 || !recipient1.value || !recipient1.value.trim()) {
              errors.push('Recipient Name (Bouquet #1) is required when gifting a bundle.');
            }
            if (!message1 || !message1.value || !message1.value.trim()) {
              errors.push('Card Message (Bouquet #1) is required when gifting a bundle.');
            }
          }

          if (errors.length > 0) {
            this.handleErrorMessage(errors.join(' '));
            // Scroll to the relevant UI (single gifting form or bundle configuration)
            const target =
              bundleSizeForValidation > 0
                ? document.querySelector('[id^="BundleConfiguration-"]') || document.querySelector('.bundle-configuration')
                : document.querySelector('.gifting-form-wrapper') ||
                document.querySelector('[id^="BundleConfiguration-"]') ||
                document.querySelector('.bundle-configuration');

            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });

              // Highlight and focus on the first missing field
              const highlightField = (field) => {
                if (field) {
                  field.style.borderColor = 'rgb(var(--color-error, 220, 38, 38))';
                  field.style.boxShadow = '0 0 0 1px rgb(var(--color-error, 220, 38, 38))';
                  const clearError = () => {
                    field.style.borderColor = '';
                    field.style.boxShadow = '';
                  };
                  field.addEventListener('input', clearError, { once: true });
                  field.addEventListener('change', clearError, { once: true });
                }
              };

              // Focus on the first missing field
              if (bundleSizeForValidation === 0) {
                const recipientName =
                  document.querySelector(`[form="${formId}"][name="properties[Recipient Name]"]`) ||
                  this.form.querySelector('[name="properties[Recipient Name]"]');
                const cardMessage =
                  document.querySelector(`[form="${formId}"][name="properties[Card Message]"]`) ||
                  this.form.querySelector('[name="properties[Card Message]"]');

                if (recipientName && (!recipientName.value || !recipientName.value.trim())) {
                  highlightField(recipientName);
                  setTimeout(() => recipientName.focus(), 300);
                } else if (cardMessage && (!cardMessage.value || !cardMessage.value.trim())) {
                  highlightField(cardMessage);
                  setTimeout(() => cardMessage.focus(), 300);
                }
              } else {
                const recipient1 = document.querySelector(`[form="${formId}"][name="properties[Recipient 1]"]`);
                const message1 = document.querySelector(`[form="${formId}"][name="properties[Message 1]"]`);

                if (recipient1 && (!recipient1.value || !recipient1.value.trim())) {
                  highlightField(recipient1);
                  setTimeout(() => recipient1.focus(), 300);
                } else if (message1 && (!message1.value || !message1.value.trim())) {
                  highlightField(message1);
                  setTimeout(() => message1.focus(), 300);
                }
              }
            }

            // Re-enable the button since we're not submitting
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
            return;
          }
        }
      }

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);

      // Get bundle size to filter properties
      // First try to get from BundleConfiguration element (most reliable)
      let bundleSize = 0;
      const bundleConfigContainer = document.querySelector('[id^="BundleConfiguration-"]');
      if (bundleConfigContainer) {
        bundleSize = parseInt(bundleConfigContainer.dataset.bundleSize) || 0;
      }

      // Fallback: Detect bundle size from form/variant selection if BundleConfiguration not found
      // This matches the logic in bundle-size-detection.liquid snippet
      if (bundleSize === 0) {
        // Check variant selection in the form
        const variantInput = this.form.querySelector('input[name="id"]:checked, input[name="id"]');
        if (variantInput) {
          const variantId = variantInput.value;
          // Try to find variant option that indicates bundle size
          const fieldsets = this.form.querySelectorAll('variant-selects fieldset, variant-radios fieldset');
          for (const fieldset of fieldsets) {
            const label = fieldset.querySelector('legend, label');
            const optionName = label ? (label.textContent || '').trim().toLowerCase() : '';

            // Check if this is a bundle size option
            if (optionName === 'bundle size' || optionName === 'size' || optionName === 'frequency') {
              const select = fieldset.querySelector('select[name^="options"]');
              const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
              const value = (select ? select.value : (checkedRadio ? checkedRadio.value : '')).toLowerCase();

              // Check in descending order (10, 8, 5) to avoid false matches
              if (value.includes('10')) {
                bundleSize = 10;
                break;
              } else if (value.includes('8')) {
                bundleSize = 8;
                break;
              } else if (value.includes('5')) {
                bundleSize = 5;
                break;
              }
            }
          }

          // If still not found, check variant title as last resort
          if (bundleSize === 0 && variantId) {
            const variantOption = this.form.querySelector(`option[value="${variantId}"]`);
            if (variantOption) {
              const variantText = variantOption.textContent.toLowerCase();
              // Check in descending order (10, 8, 5) to avoid false matches
              if (variantText.includes('10')) {
                bundleSize = 10;
              } else if (variantText.includes('8')) {
                bundleSize = 8;
              } else if (variantText.includes('5')) {
                bundleSize = 5;
              }
            }
          }
        }
      }

      // Explicitly collect fields with form attribute that match this form's ID
      // This ensures fields outside the form element are included
      // Reuse formId variable declared at line 190
      if (formId) {
        const externalFields = document.querySelectorAll(`[form="${formId}"]`);
        externalFields.forEach(field => {
          // Only add if it has a name attribute and isn't already in the form
          if (field.name && !this.form.contains(field) && !field.disabled) {
            // Filter bundle properties: 
            // - If bundleSize is 0, skip ALL bundle properties (defensive cleanup for single products)
            // - If bundleSize > 0, only include if bundle_index <= bundleSize
            if (field.dataset.bundleIndex) {
              const bundleIndex = parseInt(field.dataset.bundleIndex);
              if (bundleSize === 0 || bundleIndex > bundleSize) {
                return; // Skip bundle properties for single products or beyond bundle size
              }
            }

            if (field.type === 'checkbox' || field.type === 'radio') {
              if (field.checked) {
                formData.append(field.name, field.value || '');
              }
            } else if (field.type === 'select-one' || field.type === 'select-multiple') {
              // For select elements, include the selected value
              if (field.value) {
                formData.append(field.name, field.value);
              }
            } else {
              // For text inputs, textareas, etc. - include if not empty
              if (field.value && field.value.trim()) {
                formData.append(field.name, field.value);
              }
            }
          }
        });
      }

      // Filter bundle properties from formData itself (for fields already in the form)
      // CRITICAL: Always filter bundle properties, even if bundleSize is 0 (defensive cleanup)
      // If bundleSize is 0, remove ALL bundle properties (single product shouldn't have them)
      // If bundleSize > 0, filter based on bundle size and remove empty values
      const propertiesToRemove = [];
      for (const [key, value] of formData.entries()) {
        // Check if this is a bundle property (starts with properties[ and ends with space + number])
        if (key.match(/properties\[(Recipient|Who From|Recipient Phone|Occasion|Message) \d+\]/)) {
          // Extract the index from property names like properties[Recipient 5], properties[Recipient Phone 10], etc.
          // Match the last space followed by digits at the end
          const match = key.match(/ (\d+)\]$/);
          if (match) {
            const index = parseInt(match[1]);
            // If bundleSize is 0, remove ALL bundle properties (defensive cleanup for single products)
            // If bundleSize > 0, remove if beyond bundle size OR if empty
            if (bundleSize === 0 || index > bundleSize || !value || (typeof value === 'string' && !value.trim())) {
              propertiesToRemove.push(key);
            }
          }
          // Also check for delivery address properties like properties[Bouquet 6 - Delivery Address Line 1]
          const deliveryMatch = key.match(/properties\[Bouquet (\d+) -/);
          if (deliveryMatch) {
            const index = parseInt(deliveryMatch[1]);
            // If bundleSize is 0, remove ALL delivery address properties
            // If bundleSize > 0, remove if beyond bundle size OR if empty
            if (bundleSize === 0 || index > bundleSize || !value || (typeof value === 'string' && !value.trim())) {
              propertiesToRemove.push(key);
            }
          }
        }
      }
      // Remove properties beyond bundle size, empty bundle properties, or all bundle properties if single product
      propertiesToRemove.forEach(key => {
        formData.delete(key);
      });

      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      // splitting logic: for regular products (bundleSize === 0), force separate line items
      const quantity = parseInt(formData.get('quantity') || 1);
      if (bundleSize === 0) {
        const variantId = formData.get('id');
        const baseProperties = {};
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propKey = key.replace('properties[', '').replace(']', '');
            baseProperties[propKey] = value;
          }
        }

        const items = [];
        for (let i = 0; i < quantity; i++) {
          items.push({
            id: variantId,
            quantity: 1,
            properties: { ...baseProperties, _uid: `${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}` }
          });
        }

        const jsonBody = { items: items };
        if (this.cart) {
          jsonBody.sections = this.cart.getSectionsToRender().map((section) => section.id);
          jsonBody.sections_url = window.location.pathname;
        }

        config.body = JSON.stringify(jsonBody);
        config.headers['Content-Type'] = 'application/json';
      } else {
        config.body = formData;
      }

      // Handle Edit Mode: Remove old line item before adding new one
      if (this.editLineIndex) {
        fetch(`${routes.cart_change_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line: this.editLineIndex, quantity: 0 })
        })
          .then(() => this.executeAddToCart(config, formData))
          .catch(error => {
            console.error('Error removing item in edit mode:', error);
            this.executeAddToCart(config, formData);
          });
      } else {
        this.executeAddToCart(config, formData);
      }
    }

    executeAddToCart(config, formData) {
      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {

          if (response.status) {
            // CART ERROR EVENT
            document.dispatchEvent(
              new CustomEvent('cart:error', {
                detail: {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.description,
                  message: response.message,
                },
              })
              // -------------------------------------------
            )

            publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message });
            this.handleErrorMessage(response.description);
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          // VARIANT ADDED EVENT
          document.dispatchEvent(
            new CustomEvent("variant:add", {
              detail: {
                variant: {
                  id: formData.get("id"),
                },
                quantity: Number(formData.get("quantity") || 1),
                formElement: this.form,
                sectionId: this.dataset.section,
              },
            })
          );
          // -------------------------------------------

          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
            })
            this.error = false

            // CART CHANGE EVENT
            document.dispatchEvent(
              new CustomEvent('cart:change', {
                detail: response,
                bubbles: true,
              })
            )

            // KLAVIYO ADDED TO CART EVENT
            const responseItem = response.items ? response.items[0] : response;
            var _learnq = window._learnq || [];
            var item = {
              "Name": responseItem.title,
              "ProductID": responseItem.id,
              "ImageURL": responseItem.image,
              "URL": responseItem.url,
              "Brand": responseItem.vendor,
              "Price": responseItem.price / 100,
              "CompareAtPrice": responseItem.compare_at_price / 100,
              "Properties": responseItem.properties
            };
            _learnq.push(["track", "Added to Cart", item]);
          }

          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
